import type { HandSettlement, SettlementReceipt } from "@guandan/protocol";
import { useLocale } from "../i18n.js";
import "./hand-rewards.css";

const signed = (n: number) => `${n > 0 ? "+" : ""}${n}`;

export function HandRewards({ receipt }: { receipt?: SettlementReceipt }) {
  const { t } = useLocale();
  if (!receipt) return <p className="hand-reward-pending" role="status">{t("handRewardPending")}</p>;
  return <div className="hand-reward-receipt" role="status" aria-live="polite">
    <div className="hand-reward-values">
      <div className={receipt.coins.net < 0 ? "hand-reward-loss" : ""}><strong>{signed(receipt.coins.net)}</strong><small>{t("coins")}</small></div>
      <div><strong>{signed(receipt.xp.total)}</strong><small>XP</small></div>
      <div className={(receipt.rating?.delta ?? 0) < 0 ? "hand-reward-loss" : ""}>
        <strong>{receipt.rating ? signed(receipt.rating.delta) : "—"}</strong>
        <small>{receipt.rating ? "Elo" : t("handNotRated")}</small>
      </div>
    </div>
    <p className="hand-reward-balance">{t("handRewardCredited")} · Lv.{receipt.profileAfter.level}
      {receipt.rating && <> · {receipt.rating.before} → {receipt.profileAfter.rating} Elo</>}
    </p>
    {receipt.xp.participationMultiplier < 1 && <p className="hand-reward-note">{t("handRewardParticipation", { rate: Math.round(receipt.participation.participationRate * 100), multiplier: receipt.xp.participationMultiplier })}</p>}
    {receipt.coins.entry > 0 && <p className="hand-reward-note">{t("handStakeSettlement", { entry: receipt.coins.entry, payout: receipt.coins.payout })}</p>}
    {receipt.levelUps.length > 0 && <p className="hand-reward-level">{t("handRewardLevelUp", { level: receipt.profileAfter.level, coins: receipt.coins.levelUp })}</p>}
  </div>;
}

export function HandRewardHistory({ hands }: { hands: readonly HandSettlement[] }) {
  const { t } = useLocale();
  return <section className="hand-reward-history">
    <h3>{t("handRewardHistory")}</h3>
    <p className="hand-reward-note">{t("handRewardTotalHint")}</p>
    {hands.map(({ handNumber, receipt }) => <details key={handNumber}>
      <summary><span>{t("handRewardNumber", { hand: handNumber })}</span>
        <b>{signed(receipt.coins.net)} {t("coins")} · {signed(receipt.xp.total)} XP · {receipt.rating ? `${signed(receipt.rating.delta)} Elo` : t("handNotRated")}</b>
      </summary>
      <HandRewards receipt={receipt} />
    </details>)}
  </section>;
}
